import React, { useState } from "react"
import "./index.less"
import PopupBottomModal from '../popup-bottom-modal'
import Toast from '../toast'
import JSBridge from '@xpeng/js-bridge'
import XPMotorsApi from '@xpeng/xpmotors-jsbridge-api';
import { getClientEnv } from '../../utils/common';
import SharePosterPanel from "../share-poster-panel"

interface Props {
  visible: boolean
  title?: string
  contentClassName?: string
  shareInfo: {
    shareMainTitle: string,
    shareSubTitle: string,
    shareImgUrl: string,
    sharePageUrl?: string,
  }
  btns?: string[]
  onClose: () => void
  getPosterBase64Data?: () => Promise<string>
  onClickWx?: () => void
  onClickCopy?: () => void
  onClickPoster?: () => void
}

const SharePanel = ({
  visible,
  title = '分享至',
  contentClassName,
  shareInfo,
  btns,
  onClose,
  getPosterBase64Data = async () => '',
  onClickWx,
  onClickCopy,
  onClickPoster
}: Props) => {
  const UA = navigator.userAgent || '';
  const isXPApp = /xiaopeng/i.test(UA); // 小鹏app
  const isWX = /micromessenger/i.test(UA); // 微信
  const isWXApp = isWX && /miniProgram/i.test(UA); // 微信小程序

  const [posterUrl, setPosterUrl] = useState('')
  const [showPosterPanel, setShowPosterPanel] = useState(false)
  const [showMiniShareGuideModal, setShowMiniShareGuideModal] = useState(false)

  const copyToClipBoard = () => {
    onClickCopy && onClickCopy()

    const input = document.createElement('input');
    input.value = window.location.href;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);

    Toast.show({
      text: '已复制',
      duration: 800
    });

    onClose()
  }

  const imgToBase64 = (url: string, ratio = 1) => {
    return new Promise<any>((resolve, reject) => {
      if (url) {
        let canvas = document.createElement('canvas') as any;
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        let img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = url;
        img.onload = function() {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img,0,0);
          let dataURL = canvas.toDataURL('image/jpeg', ratio);
          resolve(dataURL);
          canvas = null;
        };
      } else {
        resolve('')
      }
    })
  }

  /**
   * 小鹏APP环境下，调用原生API分享小程序卡片给微信好友（注意：分享小程序卡片时，若host不是xiaopeng域名，点击无反应）
   * 微信小程序环境下，引导用户点击右上角胶囊按钮分享
   */
  const shareMiniProgram = async () => {
    onClickWx && onClickWx()

    // 不在APP环境下，不支持小程序分享
    // 若是微信小程序内，引导用户点击右上角胶囊按钮分享。其他环境内，toast提示在app内操作
    if (!isXPApp) {
      if (isWXApp) {
        setShowMiniShareGuideModal(true)
        onClose()
      } else {
        Toast.show({
          text: '请在小鹏APP内进行操作',
          duration: 800
        });
      }

      return
    }

    try {
      const imgBase64 = await imgToBase64(shareInfo.shareImgUrl, 0.5)
      const thumb = imgBase64.split(',')[1] || ''
      const thumbSizeKb = atob(thumb).length / 1024
      console.log('thumb size kb-->', thumbSizeKb)

      let miniprogramType = 2 // 正式版:0，测试版:1，体验版:2
      if (getClientEnv() === 'product') {
        miniprogramType = 0
      }
      const callBridge: any = JSBridge.callHandler
      callBridge.call(JSBridge, 'shareMiniProgram', {
        webpageUrl: location.href,
        miniprogramType,
        userName: "gh_bcdeac2bff9d",
        path: shareInfo?.sharePageUrl || `pages/webpage/index?pageUrl=${encodeURIComponent(location.href)}`,
        title: shareInfo.shareMainTitle || '',
        desc: shareInfo.shareSubTitle || '',
        thumb: thumbSizeKb && thumbSizeKb <= 128 ? thumb : '' // base64, 小于128k, 不需要base64类型说明头，只需要传原始图片数据
      })
    } catch (error) {
      console.log('shareMiniProgram error-->', error)
    } finally {
      onClose()
    }
  }

  const showSharePosterPanel = () => {
    onClickPoster && onClickPoster()

    if (isXPApp) {
      showXpAppSharePoster()
    } else {
      showH5SharePoster()
    }

    onClose()
  }

  const showXpAppSharePoster = async () => {
    if (!getPosterBase64Data) {
      console.error('showXpAppSharePoster error: getPosterBase64Data is required')
      return
    }

    let imgData = await getPosterBase64Data()
    console.log('generatePoster url', imgData)

    // 获取图片类型
    const match = imgData.match(/data:image\/(.*?);base64,/);
    if (!match || match.length < 2) {
      console.error('generatePoster error: invalid base64 img data')
      return
    }
    const imgType = match[1]

    setTimeout(() => {
      try {
        XPMotorsApi.sharePanel({
          style: imgData ? 4 : 1,
          addExtInfo: false,
          addUserInfo: false,
          addQrCode: false,
          data: {
            title: '',
            content: '',
            postImage: imgData ? imgData.replace(`data:image/${imgType};base64,`, '') : '',
            url: encodeURIComponent(window.location.href),
            shareType: 2
          }
        } as any).then(res => {
          console.log('XPMotorApi.sharePanel --> ', res)
        })
      } catch (error) {
        console.log('callShare error -->', error)
      }
    }, 0)
  }

  const showH5SharePoster = async () => {
    const imgData = await getPosterBase64Data()
    setPosterUrl(imgData)
    setShowPosterPanel(true)
  }

  /**
   * 默认展示的按钮code，不传默认展示微信好友和复制链接
   * wx: 微信好友
   * copy: 复制链接
   * poster: 生成海报
   */
  const defaultShowBtns = ['wx', 'copy']
  let showBtns = Array.isArray(btns) ? btns : defaultShowBtns
  // 非小鹏app或非微信小程序环境下，不展示微信好友分享按钮
  if (!(isXPApp || isWXApp)) {
    showBtns = showBtns.filter(item => item !== 'wx')
  }

  return (
    <>
      <PopupBottomModal
        title={title}
        visible={visible}
        contentClassName={contentClassName}
        onClose={onClose}
      >
        <div className="share-panel">
          {
            showBtns.includes('wx') && (
              <div className="item" onClick={shareMiniProgram}>
                <img className="icon" src={require("./assets/wx-mini.png")} alt="" />
                <div className="name">微信好友</div>
              </div>
            )
          }
          {
            showBtns.includes('copy') && (
              <div className="item" onClick={copyToClipBoard}>
                <img className="icon" src={require("./assets/copy-link.png")} alt="" />
                <div className="name">复制链接</div>
              </div>
            )
          }
          {
            showBtns.includes('poster') && (
              <div className="item" onClick={showSharePosterPanel}>
                <img className="icon" src={require("./assets/generate_poster.png")} alt="" />
                <div className="name">生成海报</div>
              </div>
            )
          }
        </div>
      </PopupBottomModal>

      <SharePosterPanel
        posterUrl={posterUrl}
        visible={showPosterPanel}
        onClose={() => setShowPosterPanel(false)}
      />

      {
        showMiniShareGuideModal && (
          <div className="mini-share-guide-modal" onClick={() => setShowMiniShareGuideModal(false)}>
            <img alt='' className='share-guide-img' src={require('./assets/mini_h5_share_guide.png')} />
          </div>
        )
      }
    </>
  )
}

export default SharePanel